import { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  CircularProgress,
  Alert,
  IconButton,
  Chip,
  useMediaQuery,
  useTheme,
  TextField,
  MenuItem,
  Grid2 as Grid,
  ToggleButtonGroup,
  ToggleButton,
  Divider,
  Tooltip,
} from '@mui/material';
import { formatDayHeader, formatMonthYear } from '../utils/formatters';
import {
  Add as AddIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  ViewWeek as ViewWeekIcon,
  ViewColumn as ViewColumnIcon,
  ViewList as ViewListIcon,
} from '@mui/icons-material';
import { useAppointments, useSchedules } from '../hooks/queries';
import AppointmentFormDialog from '../components/AppointmentFormDialog';
import AppointmentDetailsModal from '../components/AppointmentDetailsModal';
import ClassDetailsModal from '../components/ClassDetailsModal';
import AppointmentCard from '../components/AppointmentCard';
import ClassAppointmentCard from '../components/ClassAppointmentCard';
import { useContextualHint } from '../hooks/useContextualHint';
import { useSnackbar } from '../hooks/useSnackbar';
import { useDialog } from '../hooks/useDialog';
import PageLayout from '../components/ui/PageLayout';
import PageFilters from '../components/ui/PageFilters';
import AppSnackbar from '../components/ui/AppSnackbar';
import ConfirmDialog from '../components/ui/ConfirmDialog';

function getStartOfWeek(date) {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day;
  return new Date(d.setDate(diff));
}

function toDateStr(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function dateStrToDate(str) {
  const [y, mo, d] = str.split('-').map(Number);
  return new Date(y, mo - 1, d, 12, 0, 0);
}

export default function AppointmentsPage() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const daysToShow = isMobile ? 2 : 7;

  const [viewMode, setViewMode] = useState('week'); // 'week' | 'sparse' | 'list'
  const [sparseAnchorDate, setSparseAnchorDate] = useState(() => new Date());
  const [currentWeekStart, setCurrentWeekStart] = useState(getStartOfWeek(new Date()));
  const [showForm, setShowForm] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);

  const [selectedProfessional, setSelectedProfessional] = useState('all');
  const [selectedPlace, setSelectedPlace] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [showOnlyClasses, setShowOnlyClasses] = useState(false);

  const confirmDialog = useDialog();
  const markAttendedDialog = useDialog();
  const cancelDialog = useDialog();
  const detailsDialog = useDialog();
  const classDetailsDialog = useDialog();
  const [rescheduleData, setRescheduleData] = useState(null);
  const { snackbar, showSuccess, showError, hideSnackbar } = useSnackbar();

  const queryDates = useMemo(() => {
    if (viewMode === 'week') {
      const endDate = new Date(currentWeekStart);
      endDate.setDate(currentWeekStart.getDate() + daysToShow);
      return {
        startDate: currentWeekStart.toISOString().split('T')[0],
        endDate: endDate.toISOString().split('T')[0],
      };
    }
    // sparse/list: janela de 30 dias antes e depois do âncora
    const start = new Date(sparseAnchorDate);
    start.setDate(start.getDate() - 30);
    const end = new Date(sparseAnchorDate);
    end.setDate(end.getDate() + 30);
    return {
      startDate: toDateStr(start),
      endDate: toDateStr(end),
    };
  }, [viewMode, currentWeekStart, daysToShow, sparseAnchorDate]);

  const {
    appointments,
    professionals,
    places,
    loading,
    error,
    reload,
    confirmAsync,
    cancelAsync,
    markAttendedAsync,
  } = useAppointments(queryDates);

  const { schedules } = useSchedules();

  const schedulesById = useMemo(() => {
    const map = {};
    for (const schedule of schedules) map[schedule.id] = schedule;
    return map;
  }, [schedules]);

  const liveClassGroup = useMemo(() => {
    if (!classDetailsDialog.open || !classDetailsDialog.data) return null;
    const { scheduleId, slot } = classDetailsDialog.data;
    const schedule = schedulesById[scheduleId];
    const items = appointments.filter(
      apt => apt.scheduleId === scheduleId && apt.startTime === slot
    );
    return { isClass: true, schedule, items, slot };
  }, [classDetailsDialog.open, classDetailsDialog.data, appointments, schedulesById]);

  // Datas que têm pelo menos um agendamento (sem filtros — usado para selecionar dias visíveis)
  const daysWithAppointments = useMemo(() => {
    const dateSet = new Set();
    for (const apt of appointments) {
      if (!apt.startTime) continue;
      const aptDate = new Date(apt.startTime);
      if (isNaN(aptDate.getTime())) continue;
      const brt = new Date(aptDate.getTime() - 3 * 60 * 60 * 1000);
      const y = brt.getUTCFullYear();
      const mo = String(brt.getUTCMonth() + 1).padStart(2, '0');
      const d = String(brt.getUTCDate()).padStart(2, '0');
      dateSet.add(`${y}-${mo}-${d}`);
    }
    return Array.from(dateSet).sort();
  }, [appointments]);

  // 3 dias anteriores + âncora + 3 dias posteriores que têm agendamento
  const sparseVisibleDays = useMemo(() => {
    const anchorStr = toDateStr(sparseAnchorDate);
    const before = daysWithAppointments.filter(d => d < anchorStr).slice(-3);
    const after = daysWithAppointments.filter(d => d > anchorStr).slice(0, 3);
    const all = [...new Set([...before, anchorStr, ...after])].sort();
    return all.map(dateStrToDate);
  }, [sparseAnchorDate, daysWithAppointments]);

  const canSparseNavigatePrev = useMemo(() => {
    const anchorStr = toDateStr(sparseAnchorDate);
    return daysWithAppointments.some(d => d < anchorStr);
  }, [sparseAnchorDate, daysWithAppointments]);

  const canSparseNavigateNext = useMemo(() => {
    const anchorStr = toDateStr(sparseAnchorDate);
    return daysWithAppointments.some(d => d > anchorStr);
  }, [sparseAnchorDate, daysWithAppointments]);

  const hint = useContextualHint('appointments', {});

  const handlePreviousWeek = () => {
    const newStart = new Date(currentWeekStart);
    newStart.setDate(newStart.getDate() - daysToShow);
    setCurrentWeekStart(newStart);
  };

  const handleNextWeek = () => {
    const newStart = new Date(currentWeekStart);
    newStart.setDate(newStart.getDate() + daysToShow);
    setCurrentWeekStart(newStart);
  };

  const handleToday = () => {
    if (viewMode === 'week') {
      setCurrentWeekStart(getStartOfWeek(new Date()));
    } else {
      setSparseAnchorDate(new Date());
    }
  };

  const handleSparsePrevious = () => {
    const anchorStr = toDateStr(sparseAnchorDate);
    const prevDays = daysWithAppointments.filter(d => d < anchorStr);
    if (prevDays.length > 0) setSparseAnchorDate(dateStrToDate(prevDays[prevDays.length - 1]));
  };

  const handleSparseNext = () => {
    const anchorStr = toDateStr(sparseAnchorDate);
    const nextDays = daysWithAppointments.filter(d => d > anchorStr);
    if (nextDays.length > 0) setSparseAnchorDate(dateStrToDate(nextDays[0]));
  };

  const handleAppointmentCreated = () => {
    setShowForm(false);
    reload();
  };

  const getDaysArray = () => {
    const days = [];
    for (let i = 0; i < daysToShow; i++) {
      const day = new Date(currentWeekStart);
      day.setDate(currentWeekStart.getDate() + i);
      days.push(day);
    }
    return days;
  };

  const getAppointmentsForDay = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const dateStr = `${year}-${month}-${day}`;

    let filtered = appointments.filter(apt => {
      if (!apt.startTime) return false;
      const aptDate = new Date(apt.startTime);
      if (isNaN(aptDate.getTime())) return false;
      const aptDateBRT = new Date(aptDate.getTime() - 3 * 60 * 60 * 1000);
      const aptYear = aptDateBRT.getUTCFullYear();
      const aptMonth = String(aptDateBRT.getUTCMonth() + 1).padStart(2, '0');
      const aptDay = String(aptDateBRT.getUTCDate()).padStart(2, '0');
      return `${aptYear}-${aptMonth}-${aptDay}` === dateStr;
    });

    if (selectedProfessional !== 'all') {
      filtered = filtered.filter(apt =>
        apt.schedule?.professionalName &&
        professionals.find(p => p.id === selectedProfessional)?.name === apt.schedule.professionalName
      );
    }
    if (selectedPlace !== 'all') {
      filtered = filtered.filter(apt =>
        apt.schedule?.placeName &&
        places.find(p => p.id === selectedPlace)?.name === apt.schedule.placeName
      );
    }
    if (selectedStatus !== 'all') {
      filtered = filtered.filter(apt => getAppointmentStatus(apt) === selectedStatus);
    }

    return filtered;
  };

  const getAppointmentStatus = (appointment) => {
    if (appointment.cancelledAt) return 'cancelled';
    if (appointment.attendedAt) return 'completed';
    const appointmentTime = new Date(appointment.startTime);
    const now = new Date();
    if (now > appointmentTime && !appointment.attendedAt && appointment.confirmedAt) return 'no_show';
    if (appointment.confirmedAt) return 'confirmed';
    return 'pending';
  };

  const getApptCanWrite = (apt) => {
    if (!apt?.schedule?.professionalId) return true;
    const prof = professionals.find(p => p.id === apt.schedule.professionalId);
    if (!prof || !prof.isShared) return true;
    return (prof.permissions?.[2] ?? 0) >= 2;
  };

  const isToday = (date) => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const isPastDay = (date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const compareDate = new Date(date);
    compareDate.setHours(0, 0, 0, 0);
    return compareDate < today;
  };

  const groupAppointmentsBySlot = (aptsArray) => {
    const map = new Map();
    for (const apt of aptsArray) {
      const schedule = schedulesById[apt.scheduleId];
      const isClass = schedule?.maxAttendees > 1;
      const key = isClass ? `class::${apt.scheduleId}::${apt.startTime}` : `solo::${apt.id}`;
      if (!map.has(key)) map.set(key, { isClass, schedule, items: [], slot: apt.startTime });
      map.get(key).items.push(apt);
    }
    return Array.from(map.values());
  };

  const filterGroupsByClasses = (groups) => {
    if (!showOnlyClasses) return groups;
    return groups.filter(g => g.isClass === true);
  };

  // =============== AÇÕES ===============

  const handleConfirmClick = (appointment) => confirmDialog.handleOpen(appointment);

  const handleConfirmAppointment = async () => {
    const appointment = confirmDialog.data;
    try {
      await confirmAsync(appointment.id);
      showSuccess('Agendamento confirmado com sucesso!');
    } catch (err) {
      console.error('Erro ao confirmar agendamento:', err);
      showError(err.response?.data?.message || 'Erro ao confirmar agendamento');
    } finally {
      confirmDialog.handleClose();
    }
  };

  const handleMarkAttendedClick = (appointment) => markAttendedDialog.handleOpen(appointment);

  const handleMarkAttended = async () => {
    const appointment = markAttendedDialog.data;
    if (!appointment) return;
    try {
      await markAttendedAsync(appointment.id);
      showSuccess('Agendamento marcado como atendido!');
    } catch (err) {
      console.error('Erro ao marcar como atendido:', err);
      showError(err.response?.data?.message || 'Erro ao marcar como atendido');
    } finally {
      markAttendedDialog.handleClose();
    }
  };

  const handleCancelClick = (appointment) => cancelDialog.handleOpen(appointment);

  const handleCancelAppointment = async () => {
    const appointment = cancelDialog.data;
    if (!appointment) return;
    try {
      await cancelAsync({ id: appointment.id, reason: 'Cancelado pelo profissional' });
      showSuccess('Agendamento cancelado com sucesso!');
    } catch (err) {
      console.error('Erro ao cancelar agendamento:', err);
      showError(err.response?.data?.message || 'Erro ao cancelar agendamento');
    } finally {
      cancelDialog.handleClose();
    }
  };

  const handleViewDetails = (appointmentOrGroup) => {
    if (appointmentOrGroup.isClass !== undefined) {
      classDetailsDialog.handleOpen({
        scheduleId: appointmentOrGroup.items[0]?.scheduleId,
        slot: appointmentOrGroup.slot,
      });
    } else {
      detailsDialog.handleOpen(appointmentOrGroup);
    }
  };

  const handleRescheduleClick = (appointment) => {
    setRescheduleData({
      clientName: appointment.clientName,
      clientPhone: appointment.clientPhone,
      clientEmail: appointment.clientEmail,
      clientNotes: appointment.clientNotes,
    });
    setShowForm(true);
  };

  const handleFormClose = () => {
    setShowForm(false);
    setRescheduleData(null);
    setSelectedDate(null);
  };

  const renderGroups = (dayAppointments) =>
    filterGroupsByClasses(groupAppointmentsBySlot(dayAppointments)).map((g) =>
      g.isClass ? (
        <ClassAppointmentCard
          key={`class_${g.items[0].id}`}
          group={g}
          onClick={handleViewDetails}
        />
      ) : (
        <AppointmentCard
          key={g.items[0].id}
          appointment={g.items[0]}
          canWrite={getApptCanWrite(g.items[0])}
          onConfirm={handleConfirmClick}
          onCancel={handleCancelClick}
          onReschedule={handleRescheduleClick}
          onMarkAttended={handleMarkAttendedClick}
          onViewDetails={handleViewDetails}
        />
      )
    );

  const weekDays = viewMode === 'week' ? getDaysArray() : [];
  const columnDays = viewMode === 'week' ? weekDays : sparseVisibleDays;

  const navLabel = (() => {
    if (viewMode === 'week') return formatMonthYear(currentWeekStart);
    if (sparseVisibleDays.length === 0) return 'Sem agendamentos';
    const first = sparseVisibleDays[0];
    const last = sparseVisibleDays[sparseVisibleDays.length - 1];
    if (first.getMonth() === last.getMonth() && first.getFullYear() === last.getFullYear()) {
      return `${first.getDate()} – ${last.getDate()} de ${first.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' })}`;
    }
    return `${first.toLocaleDateString('pt-BR', { day: 'numeric', month: 'short' })} – ${last.toLocaleDateString('pt-BR', { day: 'numeric', month: 'short', year: 'numeric' })}`;
  })();

  return (
    <PageLayout
      themeKey="appointments"
      actionLabel="Novo Agendamento"
      onAction={() => setShowForm(true)}
      hint={hint}
    >
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <PageFilters
        onClear={() => {
          setSelectedProfessional('all');
          setSelectedPlace('all');
          setSelectedStatus('all');
          setShowOnlyClasses(false);
        }}
        clearDisabled={selectedProfessional === 'all' && selectedPlace === 'all' && selectedStatus === 'all' && !showOnlyClasses}
      >
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <TextField
            select fullWidth size="small" label="Profissional"
            value={selectedProfessional}
            onChange={(e) => setSelectedProfessional(e.target.value)}
          >
            <MenuItem value="all">Todos</MenuItem>
            {professionals.map((prof) => (
              <MenuItem key={prof.id} value={prof.id}>{prof.name}</MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <TextField
            select fullWidth size="small" label="Local"
            value={selectedPlace}
            onChange={(e) => setSelectedPlace(e.target.value)}
          >
            <MenuItem value="all">Todos</MenuItem>
            {places.map((place) => (
              <MenuItem key={place.id} value={place.id}>{place.name}</MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <TextField
            select fullWidth size="small" label="Status"
            value={selectedStatus}
            onChange={(e) => setSelectedStatus(e.target.value)}
          >
            <MenuItem value="all">Todos</MenuItem>
            <MenuItem value="pending">🟠 Não Confirmados</MenuItem>
            <MenuItem value="confirmed">🟢 Confirmados</MenuItem>
            <MenuItem value="completed">🔵 Concluídos</MenuItem>
            <MenuItem value="no_show">🔴 Não Compareceram</MenuItem>
            <MenuItem value="cancelled">⚪ Cancelados</MenuItem>
          </TextField>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <Box display="flex" alignItems="center" sx={{ pt: 1 }}>
            <Chip
              label="Apenas Turmas"
              variant={showOnlyClasses ? 'filled' : 'outlined'}
              color={showOnlyClasses ? 'primary' : 'default'}
              onClick={() => setShowOnlyClasses(!showOnlyClasses)}
              sx={{ cursor: 'pointer' }}
            />
          </Box>
        </Grid>
      </PageFilters>

      <Paper sx={{ p: 2, mb: 2 }}>
        {/* Seletor de visualização */}
        <Box display="flex" justifyContent="flex-end" mb={2}>
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            size="small"
            onChange={(_, v) => v && setViewMode(v)}
          >
            <ToggleButton value="week">
              <Tooltip title="Semana completa">
                <ViewWeekIcon fontSize="small" />
              </Tooltip>
            </ToggleButton>
            <ToggleButton value="sparse">
              <Tooltip title="Dias com agendamentos">
                <ViewColumnIcon fontSize="small" />
              </Tooltip>
            </ToggleButton>
            <ToggleButton value="list">
              <Tooltip title="Lista">
                <ViewListIcon fontSize="small" />
              </Tooltip>
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {/* Navegação */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <IconButton
            onClick={viewMode === 'week' ? handlePreviousWeek : handleSparsePrevious}
            disabled={viewMode !== 'week' && !canSparseNavigatePrev}
            size="small"
          >
            <ChevronLeftIcon />
          </IconButton>

          <Box display="flex" gap={1} alignItems="center">
            <Button size="small" variant="outlined" onClick={handleToday}>
              Hoje
            </Button>
            <Typography variant="body1" fontWeight="medium">
              {navLabel}
            </Typography>
          </Box>

          <IconButton
            onClick={viewMode === 'week' ? handleNextWeek : handleSparseNext}
            disabled={viewMode !== 'week' && !canSparseNavigateNext}
            size="small"
          >
            <ChevronRightIcon />
          </IconButton>
        </Box>

        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
            <CircularProgress />
          </Box>
        ) : viewMode === 'list' ? (
          /* ===== LISTA ===== */
          <Box>
            {sparseVisibleDays.length === 0 ? (
              <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ py: 4 }}>
                Sem agendamentos neste período
              </Typography>
            ) : (
              sparseVisibleDays.map((day, idx) => {
                const { weekday, dayNum } = formatDayHeader(day);
                const groups = filterGroupsByClasses(groupAppointmentsBySlot(getAppointmentsForDay(day)));
                return (
                  <Box key={idx}>
                    {idx > 0 && <Divider sx={{ my: 2 }} />}
                    <Box display="flex" alignItems="center" gap={1.5} mb={1.5}>
                      <Box
                        sx={{
                          width: 44,
                          height: 44,
                          borderRadius: '50%',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          bgcolor: isToday(day) ? 'primary.main' : 'grey.200',
                          color: isToday(day) ? 'white' : 'text.primary',
                          flexShrink: 0,
                        }}
                      >
                        <Typography
                          variant="caption"
                          lineHeight={1}
                          textTransform="capitalize"
                          sx={{ fontSize: '0.6rem' }}
                        >
                          {weekday.replace('.', '')}
                        </Typography>
                        <Typography variant="body2" fontWeight="bold" lineHeight={1.2}>
                          {dayNum}
                        </Typography>
                      </Box>
                      <Typography variant="subtitle1" fontWeight="medium" textTransform="capitalize">
                        {day.toLocaleDateString('pt-BR', { weekday: 'long', day: 'numeric', month: 'long' })}
                      </Typography>
                      {isToday(day) && (
                        <Chip label="Hoje" size="small" color="primary" />
                      )}
                    </Box>
                    <Box sx={{ pl: 7 }}>
                      {groups.length === 0 ? (
                        <Typography variant="caption" color="text.secondary">
                          Sem agendamentos (filtros aplicados)
                        </Typography>
                      ) : (
                        renderGroups(getAppointmentsForDay(day))
                      )}
                    </Box>
                  </Box>
                );
              })
            )}
          </Box>
        ) : (
          /* ===== SEMANA / COLUNAS SPARSE ===== */
          <Box sx={{ overflowX: 'auto' }}>
            <Box
              display="grid"
              gridTemplateColumns={`repeat(${columnDays.length || 1}, minmax(120px, 1fr))`}
              gap={1}
              sx={{ minWidth: viewMode === 'sparse' ? columnDays.length * 130 : 'unset' }}
            >
              {columnDays.map((day, index) => {
                const { weekday, dayNum } = formatDayHeader(day);
                const dayAppointments = getAppointmentsForDay(day);
                const isAnchor = viewMode === 'sparse' && toDateStr(day) === toDateStr(sparseAnchorDate);

                return (
                  <Box
                    key={index}
                    sx={{
                      border: '1px solid',
                      borderColor: isToday(day) ? 'primary.main' : 'divider',
                      borderRadius: 1,
                      overflow: 'hidden',
                      bgcolor: isToday(day) ? 'primary.50' : 'background.paper',
                    }}
                  >
                    <Box
                      sx={{
                        p: 1,
                        textAlign: 'center',
                        bgcolor: isToday(day) ? 'primary.main' : isAnchor ? 'primary.50' : 'grey.100',
                        color: isToday(day) ? 'white' : 'text.primary',
                        cursor: viewMode === 'sparse' && !isAnchor ? 'pointer' : 'default',
                      }}
                      onClick={() => {
                        if (viewMode === 'sparse' && !isAnchor) setSparseAnchorDate(day);
                      }}
                    >
                      <Typography variant="caption" display="block" textTransform="capitalize">
                        {weekday}
                      </Typography>
                      <Typography variant="h6" fontWeight="bold">
                        {dayNum}
                      </Typography>
                    </Box>

                    <Box sx={{ p: 1, minHeight: 200 }}>
                      {dayAppointments.length === 0 ? (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          textAlign="center"
                          display="block"
                          sx={{ mt: 4 }}
                        >
                          Sem agendamentos
                        </Typography>
                      ) : (
                        renderGroups(dayAppointments)
                      )}

                      {!isPastDay(day) && (
                        <Box
                          sx={{
                            mt: 1,
                            pt: 1,
                            borderTop: '1px dashed',
                            borderColor: 'divider',
                            display: 'flex',
                            justifyContent: 'center',
                          }}
                        >
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => {
                              setSelectedDate(day);
                              setShowForm(true);
                            }}
                            sx={{
                              border: '1px dashed',
                              borderColor: 'primary.main',
                              '&:hover': { bgcolor: 'primary.50' },
                            }}
                          >
                            <AddIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      )}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </Box>
        )}
      </Paper>

      <AppointmentDetailsModal
        open={detailsDialog.open}
        appointmentId={detailsDialog.data?.id}
        onClose={detailsDialog.handleClose}
        canWrite={getApptCanWrite(detailsDialog.data)}
        onConfirm={(apt) => handleConfirmClick(apt)}
        onMarkAttended={(apt) => handleMarkAttendedClick(apt)}
        onCancel={(apt) => handleCancelClick(apt)}
        onReschedule={(apt) => handleRescheduleClick(apt)}
      />

      <ClassDetailsModal
        open={classDetailsDialog.open}
        group={liveClassGroup}
        onClose={classDetailsDialog.handleClose}
        canWrite={true}
        onConfirm={(apt) => handleConfirmClick(apt)}
        onMarkAttended={(apt) => handleMarkAttendedClick(apt)}
        onCancel={(apt) => handleCancelClick(apt)}
      />

      <AppointmentFormDialog
        open={showForm}
        onClose={handleFormClose}
        onSuccess={handleAppointmentCreated}
        initialData={rescheduleData}
        preselectedDate={selectedDate}
      />

      <ConfirmDialog
        open={confirmDialog.open}
        onClose={confirmDialog.handleClose}
        onConfirm={handleConfirmAppointment}
        title="Confirmar Agendamento"
        message={<>Tem certeza que deseja confirmar o agendamento de <strong>{confirmDialog.data?.clientName}</strong>?</>}
        confirmText="Confirmar"
        confirmColor="success"
      />

      <ConfirmDialog
        open={markAttendedDialog.open}
        onClose={markAttendedDialog.handleClose}
        onConfirm={handleMarkAttended}
        title="Marcar como Atendido"
        message={<>Confirmar que <strong>{markAttendedDialog.data?.clientName}</strong> compareceu ao agendamento?</>}
        confirmText="Marcar como Atendido"
        confirmColor="info"
      />

      <ConfirmDialog
        open={cancelDialog.open}
        onClose={cancelDialog.handleClose}
        onConfirm={handleCancelAppointment}
        title="Cancelar Agendamento"
        message={<>Tem certeza que deseja cancelar o agendamento de <strong>{cancelDialog.data?.clientName}</strong>? Esta ação não pode ser desfeita.</>}
        confirmText="Sim, Cancelar"
        cancelText="Não"
        confirmColor="error"
      />

      <AppSnackbar snackbar={snackbar} onClose={hideSnackbar} />
    </PageLayout>
  );
}
